import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { motion } from 'framer-motion';
import { Send, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';

const schema = z.object({
  email: z.string().email('Please enter a valid email'),
});

type NewsletterFormData = z.infer<typeof schema>;

interface NewsletterCompactProps {
  variant?: 'inline' | 'stacked';
  className?: string;
}

export function NewsletterCompact({ variant = 'inline', className }: NewsletterCompactProps) {
  const [success, setSuccess] = React.useState(false);
  const { register, handleSubmit, formState: { errors, isSubmitting }, reset } = useForm<NewsletterFormData>({
    resolver: zodResolver(schema),
  });

  const onSubmit = async (data: NewsletterFormData) => {
    try {
      // Single capture endpoint (W2 Task 8, A7): the home-footer newsletter funnels
      // to the SAME `/.netlify/functions/subscribe-newsletter` as every other capture
      // surface — tagged `Rendezvous`, sourced `home-footer`. No Netlify-Forms path.
      const response = await fetch('/.netlify/functions/subscribe-newsletter', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...data,
          firstName: 'Friend', // Default first name for compact version
          source: 'home-footer',
          tags: ['Rendezvous'],
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to subscribe');
      }

      setSuccess(true);
      reset();

      setTimeout(() => {
        setSuccess(false);
      }, 5000);
    } catch (error) {
      console.error('Failed to subscribe:', error);
    }
  };

  if (success) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className={cn(
          "p-4 bg-olive/10 border border-olive/20 rounded-lg",
          className
        )}
      >
        <div className="flex items-center gap-2 text-olive">
          <Sparkles className="w-4 h-4" />
          <p className="text-sm font-medium">Welcome to the Rendezvous community!</p>
        </div>
        <p className="text-xs text-sand-300 mt-1">Check your inbox for a welcome message.</p>
      </motion.div>
    );
  }

  return (
    <div className={cn("space-y-3", className)}>
      <div>
        <h3 className="text-lg font-semibold text-sand-100 mb-1">Stay in the Loop</h3>
        <p className="text-sm text-sand-300">
          Get insider tips, new experiences, and exclusive member updates
        </p>
      </div>
      
      <form onSubmit={handleSubmit(onSubmit)}>
        {variant === 'inline' ? (
          <div className="flex gap-2">
            <input
              {...register('email')}
              type="email"
              className="flex-1 px-4 py-2 bg-earth-700 border border-earth-600 text-sand-100 rounded-lg 
                       placeholder:text-sand-400 focus:outline-none focus:border-olive/50 text-sm"
              placeholder="Your email address"
            />
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-olive hover:bg-olive/90 text-bone rounded-lg text-sm font-medium 
                       transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSubmitting ? (
                <span>Joining...</span>
              ) : (
                <>
                  <span>Join</span>
                  <Send className="w-3.5 h-3.5" />
                </>
              )}
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            <input
              {...register('email')}
              type="email"
              className="w-full px-4 py-2 bg-earth-700 border border-earth-600 text-sand-100 rounded-lg 
                       placeholder:text-sand-400 focus:outline-none focus:border-olive/50 text-sm"
              placeholder="Your email address"
            />
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full px-4 py-2 bg-olive hover:bg-olive/90 text-bone rounded-lg text-sm font-medium 
                       transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <span>Joining...</span>
              ) : (
                <>
                  <span>Join Newsletter</span>
                  <Send className="w-3.5 h-3.5" />
                </>
              )}
            </button>
          </div>
        )}
        
        {errors.email && (
          <p className="mt-1 text-xs text-red-400">{errors.email.message}</p>
        )}
      </form>
      
      <p className="text-xs text-sand-400">
        No spam, unsubscribe anytime. By subscribing you agree to our{' '}
        <a href="/privacy" className="underline hover:text-sand-300">privacy policy</a>.
      </p>
    </div>
  );
}
